"use client";

import * as React from "react";
import { Loader2, MapPin, X } from "lucide-react";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "@/modern/utils";
import { Command, CommandGroup, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Popover, PopoverAnchor, PopoverContent } from "@/modern/components/ui/popover";
import { useSwivuTaskLocation } from "@/react/hooks/entityHooks/useSwivuTaskLocation";
import { EntityRecord } from "@/types/entity";

interface AddressAutoCompleteProps {
  record: EntityRecord;
  recordField?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const AddressAutoComplete = ({
  record,
  recordField,
  placeholder = "Search for a location...",
  disabled = false,
  className,
}: AddressAutoCompleteProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const justSelectedRef = React.useRef(false);

  const {
    searchResults,
    onSearchAddress,
    setResolvedLocation,
    setUnresolvedLocation,
    formattedLocation,
    isLoading,
    clearSearchResults,
  } = useSwivuTaskLocation({ record, recordField });

  const [inputValue, setInputValue] = React.useState(formattedLocation.formattedAddress ?? "");

  // Keep input in sync when the record's address changes externally
  React.useEffect(() => {
    setInputValue(formattedLocation.formattedAddress ?? "");
  }, [formattedLocation.formattedAddress]);

  const hasAddress = inputValue.trim().length > 0;

  const handleSearch = (value: string) => {
    setInputValue(value);
    onSearchAddress(value);
  };

  const handleSelect = React.useCallback(
    (placeId: string) => {
      const result = searchResults.find((r) => r.value === placeId);
      justSelectedRef.current = true;
      setInputValue(result?.label ?? "");
      setResolvedLocation(placeId);
      clearSearchResults();
      setIsFocused(false);
    },
    [searchResults, setResolvedLocation, clearSearchResults]
  );

  const handleClear = React.useCallback(() => {
    setInputValue("");
    setUnresolvedLocation("");
    clearSearchResults();
    setIsFocused(false);
    inputRef.current?.focus();
  }, [setUnresolvedLocation, clearSearchResults]);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
    // Skip unresolved write when a suggestion was just picked — setResolvedLocation
    // is already in-flight and must not be overwritten with lat/lon = null.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    // Persist free-text if user typed without selecting a suggestion
    if (inputValue.trim() && inputValue !== formattedLocation.formattedAddress) {
      setUnresolvedLocation(inputValue);
    }
  }, [inputValue, formattedLocation.formattedAddress, setUnresolvedLocation]);

  // Fully controlled — never let Radix toggle open state on its own.
  // Only show when focused and there is something meaningful to display.
  const showDropdown = isFocused && !disabled && inputValue.trim().length > 0;

  return (
    <Command shouldFilter={false} className={cn("overflow-visible bg-transparent", className)}>
      <Popover open={showDropdown}>
        <div className="flex items-center gap-2 w-full">
          <PopoverAnchor asChild>
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={handleSearch}
              onFocus={() => {
                setIsFocused(true);
                if (inputValue.trim()) {
                  onSearchAddress(inputValue);
                }
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 rounded-md text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-transparent h-swivu-input px-3 py-2"
            />
          </PopoverAnchor>
          {hasAddress && !disabled && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={handleClear}
              className="flex-none p-1 hover:bg-muted rounded-sm transition-colors"
              title="Clear location"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 z-[--swivu-bryntum-z-index]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          align="start"
          sideOffset={4}
        >
          <CommandList>
            {isLoading && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching...</span>
              </div>
            )}
            {!isLoading && searchResults.length === 0 && inputValue.trim().length > 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No locations found.</div>
            )}
            {!isLoading && searchResults.length === 0 && inputValue.trim().length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">Start typing to search for locations...</div>
            )}
            {searchResults.length > 0 && (
              <CommandGroup className="max-h-64 overflow-auto">
                {searchResults.map((result) => (
                  <CommandItem
                    key={result.value}
                    value={result.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onSelect={() => handleSelect(result.value)}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{result.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
};

export default AddressAutoComplete;
